import { TProxyTabType } from "@/components/newcustomize/utils/makeProxyTabType";
import { PlanType } from "@/types/PlanTypes";
import {
  DEFAULT_CHECKOUT_PATHNAME,
  DEFAULT_CHOOSE_PLAN_PATHNAME,
  DEFAULT_CUSTOMIZE_PATHNAME,
} from "@/utils/formHref";
import type { NavItemTarget, NavItemType } from "../NavMenu/types";

/**
 * The pages of the sidebar's groups, shared by the full sidebar and the icon
 * rail (R-2295): the sidebar lists them, the rail opens the first and stays
 * selected on any of them.
 */

/** A product's key in the sidebar: a plan type, or the free plan. */
export type PlanKey = TProxyTabType | "free";

/**
 * A product's pages. An active plan's live under its id; an empty slot's only
 * page is the pricing page under the plan *type* (`/staticResidential/pricing`),
 * where the segment is a plan type, not a plan id (R-2200).
 */
export function planPages(
  plan: PlanType | undefined,
  planKey: PlanKey
): NavItemType[] {
  if (!plan) {
    return [
      {
        title: "Proxy List",
        to: "/$planId/pricing",
        params: { planId: planKey },
      },
    ];
  }

  const planId = String(plan.id);
  return [
    ...(planKey === "residential"
      ? [
          {
            title: "Endpoint Generator",
            to: "/$planId/proxy/endpoint-generator",
            params: { planId },
          } satisfies NavItemType,
        ]
      : []),
    {
      title: "Proxy List",
      to: "/$planId/proxy/list",
      params: { planId },
      alternativeTo: [
        { to: "/$planId/proxy/list/replaced", params: { planId } },
        { to: "/$planId/proxy/list/replace", params: { planId } },
      ],
    },
    {
      title: "Proxy Settings",
      to: "/$planId/settings",
      params: { planId },
    },
    {
      title: "Stats",
      to: "/$planId/stats/$tab",
      params: { planId, tab: "bandwidth" },
      alternativeTo: [
        { to: "/$planId/stats/$tab", params: { planId, tab: "bandwidth" } },
        { to: "/$planId/stats/$tab", params: { planId, tab: "error" } },
        { to: "/$planId/stats/$tab", params: { planId, tab: "activity" } },
      ],
    },
  ];
}

/** Whether `pathname` is one of the product's pages, listed above or not. */
export function isPlanSectionActive(
  pathname: string,
  plan: PlanType | undefined,
  planKey: PlanKey
): boolean {
  return pathname.startsWith(`/${plan ? plan.id : planKey}/`);
}

export const PLANS_PRICING_TARGET: NavItemTarget = {
  to: DEFAULT_CHOOSE_PLAN_PATHNAME,
};

/** Customize and Checkout keep Plans & Pricing selected. */
export const PLANS_PRICING_ALTERNATIVES: NavItemTarget[] = [
  { to: DEFAULT_CHECKOUT_PATHNAME },
  { to: DEFAULT_CUSTOMIZE_PATHNAME },
  { to: DEFAULT_CHOOSE_PLAN_PATHNAME },
];

export const SUBSCRIPTION_ITEMS: NavItemType[] = [
  {
    title: "My Plan",
    to: "/subscription",
    alternativeTo: [{ to: "/subscription/proxyreplacement" }],
  },
  {
    title: "Invoices",
    to: "/subscription/invoices",
  },
];

export const INTEGRATION_ITEMS: NavItemType[] = [
  {
    title: "MCP Server",
    to: "/integrations/mcp",
  },
  {
    title: "Skills",
    to: "/integrations/skills",
  },
  {
    title: "CLI & SDKs",
    to: "/integrations/cli-sdks",
  },
  {
    title: "API Keys",
    to: "/userapi/keys",
  },
  {
    title: "Connected Apps",
    to: "/integrations/connected-apps",
  },
  {
    title: "API Docs",
    href: "https://apidocs.webshare.io/",
    external: true,
  },
];
